import { LitElement } from 'lit'
import { v4 as uuidv4 } from 'uuid'


export const CSSMixin = (superClass) => class extends superClass {
    getHostVariable(name) {
      return getComputedStyle(this.renderRoot.host).getPropertyValue(name)
    }
    
    setHostVariable(name, value) {
      this.renderRoot.host.setProperty(name)
    }

    getHostStyle() {
        return this.renderRoot.host.style
    }
}

export const StorageMixin = (superClass) => class extends superClass {
    
  connectedCallback() {
      super.connectedCallback()
     
      let proto = Object.getPrototypeOf(this)
      for (let [name, prop] of Object.entries(proto.constructor.storage)) {
            let prop_key = this.getAttribute(name)
            window.objectStorage.subscribe(this, prop_key)

            Object.defineProperty(this, name, {
                get: function() {
                    const item = window.objectStorage.getItem(prop_key)
                    if (item) {
                        return JSON.parse(item)
                    }
                },
                set: function(newValue) {
                    window.objectStorage.setReactiveItem(prop_key, JSON.stringify(newValue))
                },
            })
      }    
  }

  disconnectedCallback() {
      super.disconnectedCallback()

      let proto = Object.getPrototypeOf(this)
      for (let [name, prop] of Object.entries(proto.constructor.storage)) {
            let prop_key = this.getAttribute(name)
            window.objectStorage.unsubscribe(this, prop_key)
      }
  }

  storageCallback() {
      this.requestUpdate()
  }
}



const Memo = (superClass) => class extends superClass {
    static __memo = {}

    getMemo(name) {
        let m = Object.getPrototypeOf(this).constructor.__memo
        if (name in m) {
            return m[name]
        }
        m[name] = {}
        return m[name]
    }

    get memo() {
        return Object.getPrototypeOf(this).constructor.__memo
    }
}


export const BroadcastMember =  (space, superClass) => class extends superClass {

    static __key = "BroadcastMemberClass"
    static __channels = {}
    static __module = {}
    static __pipeline = {}

    constructor() {
        super()
        this.keys = []
        this.namespace = uuidv4()
    }

    broadcast(key, payload) {
        let channels = Object.getPrototypeOf(this).constructor.__channels        
        if (channels[key]) {
            for (let handler of Object.values(channels[key])) {
                handler(payload)
            }
        }
    }

    subscribe(key, handler) {
        let channels = Object.getPrototypeOf(this).constructor.__channels
        if (!channels[key]) {
            channels[key] = {}
        }
        channels[key][this.namespace] = handler
        this.keys.push(key)
    }


    disconnectedCallback() {
        super.disconnectedCallback()

        let channels = Object.getPrototypeOf(this).constructor.__channels 
        for (let key of this.keys) {
            delete channels[key][this.namespace]
        }
    }

    addModule(name, handler) {
        Object.getPrototypeOf(this).constructor.__module[name] = handler
    }

    callModule(name, ...args) {
        return Object.getPrototypeOf(this).constructor.__module[name](...args)
    }

    addPipeline(name, handler) {
        let pipeline = Object.getPrototypeOf(this).constructor.__pipeline[name]
        if (pipeline) {
            pipeline.push(handler)
        } else {
            Object.getPrototypeOf(this).constructor.__pipeline[name] = [handler]
        }
    }

    *callPipeline(name, ...args) {
        let pipeline = Object.getPrototypeOf(this).constructor.__pipeline[name]
        if (pipeline && pipeline.length) {
            for (const handler of pipeline) {
                yield handler(...args)
            }
        }
    }
}


export const Pager =  (superClass) => class extends superClass {

    constructor() {
        super()
    }

    initPage(name, mode) {
        this.pageName = name
        window.addEventListener("popstate", evt => {
            if (evt.state) {
                if (evt.state.page != name) {
                    this.style.display = `none`
                } else {
                    this.style.display = mode
                }

            }
        })
    }

    pushPage(data) {
        history.pushState({page: this.pageName, data: data}, this.pageName, "")
    }
}


export const Spinner =  (superClass) => class extends superClass {

    showSpinner(target) {
        let box = target ?? this.parentElement

        let spinner = box.querySelector("good-spinner") 
        if (!spinner) {
            spinner = document.createElement("good-spinner")
            box.appendChild(spinner)
        }
    }

    hideSpinner(target) {
        let box = target ?? this.parentElement 
        let spinner = box.querySelector("good-spinner")
        if (spinner) {
            spinner.remove()
        }
    }

    showShadowSpinner() {
        this.showSpinner(this.renderRoot)
    }

    hideShadowSpinner() {
        this.hideSpinner(this.renderRoot)
    }
}


export function closestElementComposed(selector, base) {
    if (base) {        
        let parent = base.parentElement || base.getRootNode().host
        if (parent && parent.matches(selector)) {
            return parent
        } else {
            return closestElementComposed(selector, parent)
        }
    }
}


export class AppElement extends Memo(Pager(BroadcastMember("app", LitElement))) {
    getContext(key) {
        if (this.isConnected) {
            let ctx = closestElementComposed("app-context", this)
            return ctx && ctx.context[key]
        } else {
            console.error("disconnected context call from ", this)
        }
    }

    setContext(key, value) {
        if (this.isConnected) {
            let ctx = closestElementComposed("app-context", this)
            if (ctx && ctx.listeners[key] && ctx.listeners[key].length) {
                for (let callback of ctx.listeners[key]) {
                    callback(value)
                }
            }
            ctx.context[key] = value
        } else {
            console.error("disconnected context call from ", this)
        }
    }


    listenContext(key, callback) {
        if (this.isConnected) {
            let ctx = closestElementComposed("app-context", this)
            if (ctx) {
                if (ctx.listeners[key]) {
                    ctx.listeners[key].push(callback)
                } else {
                    ctx.listeners[key] = [callback]
                }
            }
        } else {
            console.error("disconnected context call from ", this)
        }
    }
}